// Copyright ©️ 2026 eVolve MEP, LLC

import { ActionIcon, type MantineSize } from '@mantine/core';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';

type Props = {
  checked: boolean;
  onToggle: () => void;
  /** When checked, describes what clicking (i.e., unchecking it) will do.*/
  actionLabelWhenChecked: string;
  /** When unchecked, describes what clicking (i.e., checking it) will do.*/
  actionLabelWhenUnchecked: string;
  icon: EvolveIconName;
  /* @defaultValue 'md' */
  size?: MantineSize;
  disabled?: boolean;
};

const sizeToIconMap: Record<MantineSize, `${number}rem`> = {
  xs: '0.500rem',
  sm: '0.700rem',
  md: '0.900rem',
  lg: '1.000rem',
  xl: '1.250rem',
};

/** An ActionIcon button that toggles like a checkbox.*/
export const ToggleButton = ({
  checked,
  onToggle,
  actionLabelWhenChecked,
  actionLabelWhenUnchecked,
  icon,
  size = 'md',
  disabled,
}: Props) => {
  const label = checked ? actionLabelWhenChecked : actionLabelWhenUnchecked;
  return (
    <AgGridStyleTooltip label={label} withArrow>
      <ActionIcon
        variant={checked ? 'filled' : 'light'}
        onClick={onToggle}
        aria-label={label}
        size={size}
        disabled={disabled}
      >
        <EvolveIcon icon={icon} size={sizeToIconMap[size]} />
      </ActionIcon>
    </AgGridStyleTooltip>
  );
};
